import React, {FC, useContext, useEffect, useMemo} from "react";
import {T, useTranslate} from "@tolgee/react";
import {
  Box,
  ButtonBase,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import RemoveCirceIcon from "@material-ui/icons/RemoveCircle";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import {FullProduct, ProductNoteAttachment} from "ts/api/product";
import formatPrice from "tools/formats/formatPrice";
import {grey} from "@material-ui/core/colors";
import {CurrencyContext} from "view/context/CurrencyContext";
import FlexBox from "view/components/Layout/FlexBox";
import LinkedPropertyTooltip from "view/components/ShoppingCart/components/LinkedPropertyTooltip";
import {LinkedPropertyResult} from "tools/product/productLinkedProperties";
import {generateTestId} from "tools/cypress/generateTestId";
import ProductItemCpoConfigRow from "view/components/ShoppingCart/components/ProductItem/ConfigRow";
import {
  CustomProductOptionConfigType,
  ProductType,
} from "@evennt-tech/api-v2-core";
import {getCpoConfigAvailableQty} from "tools/custom-product-option/common";
import {useProductFiles} from "store/custom-product-option/selectors";
import {findOptionImageUrl} from "tools/product/image/findOptionImageUrl";
import CartDiscountCodes from "view/components/ShoppingCart/components/ProductItem/DiscountCode";
import {useQuickOrderId} from "hooks/show/order";
import SafeHtml from "view/components/SafeHtml";
import DataRow from "view/components/DataRow";
import formatBytes from "tools/formats/formatBytes";
import FileIcon from "view/components/FileIcon";
import NewTabIcon from "@material-ui/icons/OpenInNew";
import {useMergedProductDiscountCodes} from "view/apps/Exhibitor/pages/Cart/helpers/useMergedProductDiscountCodes";
import {
  useExhibitorProductPurchaseCount,
  useRemainingTime,
} from "hooks/product";
import {useShowProductsStore} from "store/product/show-products.store";
import {parseISO} from "date-fns";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import useProductImagePlaceholder from "hooks/useProductImagePlaceholder";
import ProductNotesBlock from "../ProductNotesBlock";
import Alert from "../../../Alert";
import useStyles from "../../styles";
import FeeItem from "../FeeItem";

export type ProductItemProps = {
  product: FullProduct;
  showFees?: boolean;
  onRemoveProduct: (id: string) => void;
  onNoteChange: (productId: string, note: string) => void;
  onChangeQuantity: (id: string, quantity: number) => void;
  onAttachmentModalOpen: (
    productId: string,
    firstAttachment?: ProductNoteAttachment | undefined
  ) => void;
  linkedProperty?: LinkedPropertyResult | null;
  idx: number;
};

export const isNotAvailableForPurchase = (
  product: FullProduct,
  purchaseCount: number
) => {
  const selectedConfig = product.configs?.find(
    (c) => c.id === product.cpoConfigId
  );

  if (!selectedConfig) {
    return null;
  }
  const quantity = product.cartQuantity ?? 1;
  if (product.purchaseLimit !== null) {
    const remainingAllowedPurchases = Math.max(
      0,
      product.purchaseLimit - purchaseCount
    );
    if (quantity > remainingAllowedPurchases) {
      return "purchaseLimit";
    }
  }

  const configAvailableQuantity = getCpoConfigAvailableQty(selectedConfig);

  if (Math.ceil(product.cartQuantity ?? 1) > configAvailableQuantity) {
    return "stock";
  }
  return null;
};

const ProductItem: FC<ProductItemProps> = ({
  product,
  showFees,
  onNoteChange,
  onChangeQuantity,
  onRemoveProduct,
  onAttachmentModalOpen,
  linkedProperty,
  idx,
}) => {
  const quickOrderId = useQuickOrderId();
  const classes = useStyles();
  const {t} = useTranslate();
  const placeholder = useProductImagePlaceholder();
  const currency = useContext(CurrencyContext);
  const {show} = useShowProductsStore();
  const updateProductConfigsById = useShowProductsStore(
    (store) => store.updateConfigsByProductId
  );
  const productPurchaseCount = useExhibitorProductPurchaseCount(product.id);
  const cartQuantity = product.cartQuantity || 1;

  const theme = useTheme();

  const selectedConfig = useMemo(() => {
    const {configs, cpoConfigId} = product;
    if (!configs?.length || !cpoConfigId) {
      return null;
    }

    return configs.find((c) => c.id === cpoConfigId) ?? null;
  }, [product]);
  const selectedCustomConfig =
    selectedConfig?.type === CustomProductOptionConfigType.CUSTOM
      ? selectedConfig
      : null;
  const maxQuantity = selectedConfig
    ? getCpoConfigAvailableQty(selectedConfig)
    : 0;
  const purchaseLimitExist = product.purchaseLimit !== null;

  const isLimitReached =
    !quickOrderId &&
    purchaseLimitExist &&
    cartQuantity >= Math.max(0, product.purchaseLimit! - productPurchaseCount);
  const shoppingLimit = purchaseLimitExist
    ? Math.min(product.purchaseLimit! - productPurchaseCount, maxQuantity)
    : maxQuantity;

  const remainingTime = useRemainingTime({
    surchargeStages: product.surcharges,
    deliveryStartMS: show?.deliveryStart
      ? parseISO(show.deliveryStart)?.getTime()
      : 0,
    basePrice:
      (product.showPrice ?? product.basePrice ?? 0) +
      (selectedCustomConfig?.priceUplift ?? 0),
    organiserFee: product.organiserFee,
    supplierFee: product.ownerToShow?.organiserFee ?? 0,
    currency: show?.currency || (currency as any),
  });

  useEffect(() => {
    updateProductConfigsById(product.id);
  }, [product.id, remainingTime.formatted, updateProductConfigsById]);

  const images = useProductFiles(product.owner.id, product.id);

  const purchaseBlockedReason = quickOrderId
    ? null
    : isNotAvailableForPurchase(product, productPurchaseCount);

  const productDiscountCodes = useMergedProductDiscountCodes(
    product.discountCodes
  );

  const {name: linkedPropertyName, unit} = linkedProperty || {};
  const {id} = generateTestId("exhibitor-cart-product", idx);

  const imageUrl =
    findOptionImageUrl(images, product.imageId) || product.image?.file?.url;

  return (
    <Box id={id}>
      <Box className={classes.product}>
        <Box className={classes.productImage}>
          <ButtonBase focusRipple className={classes.imageButton}>
            <img
              src={imageUrl || placeholder}
              alt={product.name}
              width={40}
              height={40}
              className={classes.image}
            />
          </ButtonBase>
        </Box>
        <Box className={classes.productContent}>
          <Box className={classes.productMainInfo}>
            <Tooltip title={product.name}>
              <Typography variant="body2" className={classes.productName}>
                {product.name}
                {product.type === ProductType.QUICK
                  ? t("common.shopping_cart.custom_product_suffix")
                  : ""}
              </Typography>
            </Tooltip>
            <Box
              display="flex"
              alignItems="center"
              justifyContent={linkedPropertyName ? "space-between" : "center"}
            >
              {!!linkedPropertyName && (
                <LinkedPropertyTooltip propertyName={linkedPropertyName} />
              )}
              <Box className={classes.productActions}>
                <IconButton
                  aria-label={t("common.aria.decrease")}
                  onClick={() => {
                    onChangeQuantity(product.id, cartQuantity - 1);
                  }}
                  disabled={cartQuantity < 2 || !!quickOrderId}
                >
                  <RemoveIcon
                    htmlColor={theme.palette.contrastIcon.main}
                    fontSize="small"
                  />
                </IconButton>
                <Box
                  display="flex"
                  justifyContent="center"
                  maxWidth="fit-content"
                >
                  <Typography>
                    {cartQuantity} {unit && unit}
                  </Typography>
                </Box>
                <IconButton
                  aria-label={t("common.aria.increase")}
                  onClick={() => {
                    onChangeQuantity(product.id, cartQuantity + 1);
                  }}
                  disabled={cartQuantity >= shoppingLimit || !!quickOrderId}
                >
                  <AddIcon
                    htmlColor={theme.palette.contrastIcon.main}
                    fontSize="small"
                  />
                </IconButton>
              </Box>
              <Box width="80px" textAlign="right">
                <Typography>
                  {formatPrice(product.priceWithFees * cartQuantity, currency)}
                </Typography>
              </Box>
            </Box>
          </Box>
          {remainingTime.nextStage && (
            <Box className={classes.remainingTimeWrapper}>
              <TrendingUpIcon className={classes.remainingTimeIcon} />
              <Typography className={classes.remainingTimeWrapperText}>
                <T
                  keyName="common.shopping_cart.price_increases_to"
                  params={{time: remainingTime.formatted}}
                />
              </Typography>
            </Box>
          )}
        </Box>
        <Box className={classes.productRemove}>
          <Tooltip title={t("common.shopping_cart.remove_from_cart")}>
            <IconButton
              aria-label={t("common.aria.remove")}
              onClick={() => onRemoveProduct(product.id)}
              disabled={!!quickOrderId}
            >
              <RemoveCirceIcon style={{color: grey[500]}} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {isLimitReached && (
        <FlexBox gap={1} alignCenter>
          <InfoOutlined className={classes.purchaseLimitIcon} />
          <Typography variant="h5" className={classes.purchaseLimitLabel}>
            <T keyName="common.shopping_cart.limit_reached" />
          </Typography>
        </FlexBox>
      )}
      {showFees &&
        product.fees?.map((fee) => <FeeItem key={fee.id} fee={fee} />)}
      {selectedCustomConfig && (
        <ProductItemCpoConfigRow config={selectedCustomConfig} />
      )}
      {!!productDiscountCodes.length && (
        <Box mt={1.5}>
          <CartDiscountCodes discountCodes={productDiscountCodes} />
        </Box>
      )}
      {!quickOrderId && (
        <ProductNotesBlock
          product={product}
          onNoteChange={onNoteChange}
          onAttachmentModalOpen={onAttachmentModalOpen}
          id={id}
        />
      )}
      {!!quickOrderId && !!product.description && (
        <SafeHtml
          className={classes.productDescription}
          html={product.description}
        />
      )}
      {!!quickOrderId && !!product.attachments?.length && (
        <FlexBox column gap={1} mt={1}>
          {product.attachments.map((attachment) => (
            <DataRow
              key={attachment.id}
              value={attachment.file?.title?.split(".").shift()}
              label={`pdf, ${formatBytes(attachment.file?.size ?? 0)}`}
              icon={<FileIcon extension="pdf" />}
              actionPanel={
                <IconButton
                  style={{padding: 8}}
                  component="a"
                  href={attachment.file?.url}
                  rel="noreferrer"
                  target="_blank"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <NewTabIcon htmlColor={grey[500]} />
                </IconButton>
              }
              classes={{
                // label: classes.fileAttachmentRowExtension,
                root: classes.fileAttachmentRowRoot,
              }}
              last
            />
          ))}
        </FlexBox>
      )}
      {purchaseBlockedReason === "stock" && (
        <Alert severity="warning" className={classes.warning}>
          <T keyName="common.shopping_cart.not_enough_stock" />
        </Alert>
      )}
    </Box>
  );
};

export default ProductItem;
